import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Card, Input, Space, Switch, Table, Tag, Typography, message } from "antd";
import { CopyOutlined, ReloadOutlined } from "@ant-design/icons";
import { getData, getErrorMessage, rfidAdminApi } from "../../hooks/rfidAdminApi";
import "./RfidAdmin.css";

const { Paragraph, Text, Title } = Typography;
const configuredApiBase = String(process.env.REACT_APP_PUBLIC_API_URL || process.env.REACT_APP_API_BASE_URL || "").trim();
const fallbackIngestBase = (() => {
  try {
    if (configuredApiBase) return new URL(configuredApiBase, window.location.origin).origin;
  } catch (_) {}
  return window.location.origin;
})();
const defaultIngestUrl = String(
  process.env.REACT_APP_RFID_INGEST_URL
  || `${fallbackIngestBase}/rfid`
).trim();

const ReaderAdmin = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const [readers, setReaders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [ingestUrl, setIngestUrl] = useState(defaultIngestUrl);

  const load = async () => {
    try {
      setLoading(true);
      setReaders(getData(await rfidAdminApi.getReaders()));
    } catch (error) {
      messageApi.error(getErrorMessage(error, "리더 상태를 불러오지 못했습니다."));
    } finally {
      setLoading(false);
    }
  };

  const loadIngestConfig = async () => {
    try {
      const res = await rfidAdminApi.getIngestConfig();
      if (res?.data?.ingest_url) setIngestUrl(res.data.ingest_url);
      else if (res?.data?.message) messageApi.warning(res.data.message);
    } catch (_) {
      // Keep the environment/browser-derived fallback URL visible if the
      // server is an older version without the compatibility endpoint.
    }
  };

  useEffect(() => {
    load();
    loadIngestConfig();
    const timer = setInterval(load, 3000);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleReader = async (record, checked) => {
    try { await rfidAdminApi.updateReader(record.id, { isActive: checked }); await load(); }
    catch (error) { messageApi.error(getErrorMessage(error)); }
  };

  const columns = useMemo(() => [
    { title: "리더명", dataIndex: "name" },
    { title: "설치 위치", dataIndex: "location", render: (v) => v || "-" },
    { title: "마지막 수신", dataIndex: "last_seen_at", width: 180, render: (v) => v ? new Date(v).toLocaleString() : <Tag>아직 미수신</Tag> },
    { title: "상태", dataIndex: "last_payload_status", width: 160, render: (v) => {
      const color = v === "PROCESSED" ? "green" : v === "DUPLICATE_ONLY" ? "blue" : v === "RECEIVED_NO_EPC" ? "red" : "default";
      return <Tag color={color}>{v || "미수신"}</Tag>;
    } },
    { title: "최근 처리", width: 110, render: (_, r) => `${Number(r.last_received_count || 0)}건 / ${Number(r.last_accepted_count || 0)}건` },
    { title: "마지막 IP", dataIndex: "last_ip", width: 150, render: (v) => v || "-" },
    { title: "활성", dataIndex: "is_active", width: 80, render: (v, r) => <Switch checked={Boolean(v)} onChange={(checked) => toggleReader(r, checked)} /> },
  ], []);

  const copyUrl = async () => {
    await navigator.clipboard.writeText(ingestUrl);
    messageApi.success("ATF862 입력 주소를 복사했습니다.");
  };

  return (
    <div className="rfid-page">
      {contextHolder}
      <div className="rfid-page-header">
        <div>
          <h1 className="rfid-page-title">리더 연결 설정</h1>
          <p className="rfid-page-desc">리더 키 발급 없이 아래 주소 하나만 ATF862에 한 번 저장하면 됩니다.</p>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>새로고침</Button>
      </div>

      <Card>
        <Title level={4}>ATF862에서 넣을 위치</Title>
        <Paragraph>
          <b>Master Mode → HTTP POST Communication Settings → POST URL</b> 칸에 아래 주소를 넣고
          <b> Enable 체크 → Save</b>를 누르세요.
        </Paragraph>
        <Input.TextArea value={ingestUrl} onChange={(event) => setIngestUrl(event.target.value)} autoSize={{ minRows: 2 }} />
        <Button type="primary" icon={<CopyOutlined />} onClick={copyUrl} style={{ marginTop: 10 }}>주소 복사</Button>
        <Alert
          style={{ marginTop: 16 }}
          type="info"
          showIcon
          message="집 테스트"
          description="현재 PC와 리더기가 직결되어 있으면 보통 http://192.168.9.10:8080/rfid 를 사용합니다."
        />
        <Alert
          style={{ marginTop: 10 }}
          type="warning"
          showIcon
          message="학교·클라우드 운영"
          description="클라우드 서버 주소와 장치 토큰을 합친 URL을 사용합니다. 예: https://api.example.com/rfid/장치토큰. 이 값은 배포 환경변수로 한 번만 정해두고 현장에는 완성된 주소만 전달합니다."
        />
      </Card>

      <Card className="rfid-section-gap" title="수신 상태">
        <Table rowKey="id" loading={loading} dataSource={readers} columns={columns} pagination={{ pageSize: 10 }} scroll={{ x: 1000 }} />
        {!loading && readers.length === 0 && (
          <Text type="secondary">처음 태그가 서버에 도착하면 ‘기본 ATF862’ 리더가 자동 생성됩니다.</Text>
        )}
      </Card>
    </div>
  );
};

export default ReaderAdmin;
